import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { resolvePageHelpTopic } from "../helpers/resolvePageHelp.js";

/** @typedef {import('../helpers/resolvePageHelp.js').PageHelpTopic} PageHelpTopic */
/** @typedef {import('../helpers/resolvePageHelp.js').PageHelpContext} PageHelpContext */

const HelpContext = createContext(null);

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {PageHelpContext} props.pageContext
 * @param {Record<string, PageHelpTopic>} props.topicRegistry
 * @param {PageHelpTopic} [props.fallbackTopic]
 */
export function HelpProvider({ children, pageContext, topicRegistry, fallbackTopic }) {
  const [open, setOpen] = useState(false);

  const topic = useMemo(
    () => resolvePageHelpTopic(pageContext, topicRegistry, fallbackTopic),
    [pageContext, topicRegistry, fallbackTopic],
  );

  const openHelp = useCallback(() => setOpen(true), []);
  const closeHelp = useCallback(() => setOpen(false), []);
  const toggleHelp = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo(
    () => ({
      open,
      pageContext,
      topic,
      openHelp,
      closeHelp,
      toggleHelp,
    }),
    [open, pageContext, topic, openHelp, closeHelp, toggleHelp],
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp() {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error("useHelp requires HelpProvider");
  return ctx;
}

export function useHelpOptional() {
  return useContext(HelpContext);
}
